// Fixtures do contrato único de `/me` (Parte 3.6) para o FakeAuthProvider — uma por linha da matriz 2×3 de 14.7.
// Cenários: none | single | multi | network-only | network+multi | network-quality | network-dpo | tenant-investigator |
// network-people | network-legal | tenant-admin | tenant-break-glass | tenant-delegate | tenant-lead-coordinator (`VITE_FAKE_ME_SCENARIO`); `network-quality`/`network-dpo`/`tenant-investigator` (GIY-056) são
// a QA e o DPO das duas chaves da matriz e o investigador principal de um estudo do centro-âncora (break-glass e auditoria
// de estudo); `tenant-delegate`/`tenant-lead-coordinator` (GIY-075) são a delegada (contexto só pela delegação) e a coordenadora líder do
// delegation log; `network-people`/`network-legal` (GIY-067) são o RH da rede (`PEOPLE_ADMIN`) e o Jurídico (`LEGAL`) das duas
// chaves da Matriz de Enquadramento e da exceção de transição.
import type { MeContext, MeResponse, MeUser } from '@giyus/api-client';

import type { FakeMeScenario } from '../env';

export const FIXTURE_IDS = {
  tenantPilot: '01926f3e-0000-7000-8000-00000000000a',
  tenantSecond: '01926f3e-0000-7000-8000-00000000000b',
  sitePilotSede: '01926f3e-0000-7000-8000-0000000000a1',
  sitePilotAnexo: '01926f3e-0000-7000-8000-0000000000a2',
  siteSecondUnidade: '01926f3e-0000-7000-8000-0000000000b1',
  legalEntityPilot: '01926f3e-0000-7000-8000-0000000000e1',
  legalEntitySecond: '01926f3e-0000-7000-8000-0000000000e2',
  /** Estudo do centro-âncora (StudySiteAssignment) — UUID válido: é digitado em formulários que validam o formato. */
  studyPilot: '01926f3e-0000-7000-8000-00000000e5d1',
  /** Estudo aberto em emergência (break-glass) no cenário `tenant-break-glass`. */
  studyEmergency: '01926f3e-0000-7000-8000-00000000e5d2',
  /** Segundo estudo do centro-âncora (em start-up): tem a delegação pendente de aceite do cenário `tenant-delegate`. */
  studyPilotSecond: '01926f3e-0000-7000-8000-00000000e5d3',
} as const;

/** Vencimento do break-glass do cenário `tenant-break-glass`: bem no futuro, para a faixa não expirar com o relógio real (o teste da expiração fixa o relógio antes dele). */
export const BREAK_GLASS_FIXTURE_EXPIRES_AT = '2099-09-23T17:02:11Z';

export const FIXTURE_TENANT_CODES = { pilot: 'PILOT', second: 'SECOND' } as const;

/** Usuários de rede das duas chaves da matriz (GIY-056): os mesmos ids do estado falso de autorização. */
export const FIXTURE_NETWORK_USERS = {
  quality: '01926f3e-0000-7000-8000-0000000000u5',
  dpo: '01926f3e-0000-7000-8000-0000000000u6',
} as const;

/** Principais de rede de Pessoas (GIY-067): usuários e pessoas com ids hexadecimais (campos UUID validam o formato). */
export const FIXTURE_PEOPLE = {
  adminUser: '01926f3e-0000-7000-8000-00000000a0c1',
  adminPerson: '01926f3e-0000-7000-8000-00000b00a0c1',
  adminLink: '01926f3e-0000-7000-8000-00000c00a0c1',
  legalUser: '01926f3e-0000-7000-8000-00000000a0c2',
  legalPerson: '01926f3e-0000-7000-8000-00000b00a0c2',
  legalLink: '01926f3e-0000-7000-8000-00000c00a0c2',
  /** Administradora do centro piloto, sem acesso de rede (cenário `tenant-admin`). */
  centerAdminUser: '01926f3e-0000-7000-8000-00000000a0c3',
  centerAdminPerson: '01926f3e-0000-7000-8000-00000b00a0c3',
  centerAdminLink: '01926f3e-0000-7000-8000-00000c00a0c3',
} as const;

/** Principais do delegation log (GIY-075): o delegado (enfermeira) e a coordenadora líder do estudo piloto; ids hexadecimais. */
export const FIXTURE_DELEGATION = {
  delegateUser: '01926f3e-0000-7000-8000-00000000a0d1',
  delegatePerson: '01926f3e-0000-7000-8000-00000b00a0d1',
  delegateLink: '01926f3e-0000-7000-8000-00000c00a0d1',
  coordinatorUser: '01926f3e-0000-7000-8000-00000000a0d2',
  coordinatorPerson: '01926f3e-0000-7000-8000-00000b00a0d2',
  coordinatorLink: '01926f3e-0000-7000-8000-00000c00a0d2',
} as const;

const session = {
  id: '01926f3e-0000-7000-8000-0000000000f1',
  authTime: '2026-09-23T13:02:11Z',
  idleExpiresAt: '2026-09-23T13:32:11Z',
  absoluteExpiresAt: '2026-09-24T01:02:11Z',
} as const;

function user(
  id: string,
  email: string,
  displayName: string,
  locale: MeUser['preferredLocale'],
): MeUser {
  return {
    id,
    kind: 'Human',
    email,
    displayName,
    preferredLocale: locale,
    mfa: { enrolled: true, methods: ['Totp', 'RecoveryCodes'] },
  };
}

function pilotContext(
  options: {
    twoSites?: boolean;
    roles?: MeContext['effectiveRoles'];
    breakGlass?: MeContext['activeBreakGlass'];
    activeLinks?: MeContext['activeLinks'];
    accessibleStudySiteCount?: number;
  } = {},
): MeContext {
  const sites: MeContext['sites'] = [
    {
      id: FIXTURE_IDS.sitePilotSede,
      code: 'SEDE',
      name: 'Sede',
      timeZoneId: 'America/Sao_Paulo',
    },
  ];
  return {
    tenantId: FIXTURE_IDS.tenantPilot,
    code: FIXTURE_TENANT_CODES.pilot,
    name: 'Centro-âncora (piloto)',
    sites: options.twoSites
      ? [
          ...sites,
          {
            id: FIXTURE_IDS.sitePilotAnexo,
            code: 'ANEXO',
            name: 'Anexo Manaus',
            timeZoneId: 'America/Manaus',
          },
        ]
      : sites,
    effectiveRoles: options.roles ?? [
      { scopeType: 'Tenant', scopeId: FIXTURE_IDS.tenantPilot, role: 'STUDY_COORDINATOR' },
    ],
    accessibleStudySiteCount: options.accessibleStudySiteCount ?? 3,
    activeLinks: options.activeLinks ?? [
      {
        id: '01926f3e-0000-7000-8000-0000000000l1',
        linkType: 'CooperadoCPESQ',
        functionCode: 'STUDY_COORDINATOR',
        primarySiteId: FIXTURE_IDS.sitePilotSede,
      },
    ],
    activeBreakGlass: options.breakGlass ?? [],
  };
}

function secondContext(): MeContext {
  return {
    tenantId: FIXTURE_IDS.tenantSecond,
    code: FIXTURE_TENANT_CODES.second,
    name: 'Centro parceiro',
    sites: [
      {
        id: FIXTURE_IDS.siteSecondUnidade,
        code: 'UNIDADE-1',
        name: 'Unidade 1',
        timeZoneId: 'America/Sao_Paulo',
      },
    ],
    effectiveRoles: [
      { scopeType: 'Site', scopeId: FIXTURE_IDS.siteSecondUnidade, role: 'STUDY_COORDINATOR' },
    ],
    accessibleStudySiteCount: 1,
    activeLinks: [
      {
        id: '01926f3e-0000-7000-8000-0000000000l2',
        linkType: 'PJ',
        functionCode: 'STUDY_COORDINATOR',
        primarySiteId: FIXTURE_IDS.siteSecondUnidade,
      },
    ],
    activeBreakGlass: [],
  };
}

/** Constrói a resposta de `/me` do cenário pedido (sempre um objeto novo). */
export function buildMeFixture(scenario: FakeMeScenario): MeResponse {
  switch (scenario) {
    case 'none':
      return {
        user: user(
          '01926f3e-0000-7000-8000-0000000000u0',
          'bruno@centro.example',
          'Bruno Lima',
          'en-US',
        ),
        person: {
          id: '01926f3e-0000-7000-8000-0000000000p0',
          activeLinks: [
            {
              id: '01926f3e-0000-7000-8000-0000000000l3',
              legalEntityId: FIXTURE_IDS.legalEntityPilot,
              tenantId: FIXTURE_IDS.tenantPilot,
              linkType: 'CLT',
              functionCode: 'RECEPTIONIST',
              primarySiteId: FIXTURE_IDS.sitePilotSede,
            },
          ],
        },
        networkAccess: { hasAccess: false, roles: [] },
        contexts: [],
        defaultContextHint: null,
        session,
      };
    case 'single':
      return {
        user: user(
          '01926f3e-0000-7000-8000-0000000000u1',
          'ana@centro.example',
          'Ana Souza',
          'pt-BR',
        ),
        person: {
          id: '01926f3e-0000-7000-8000-0000000000p1',
          activeLinks: [
            {
              id: '01926f3e-0000-7000-8000-0000000000l1',
              legalEntityId: FIXTURE_IDS.legalEntityPilot,
              tenantId: FIXTURE_IDS.tenantPilot,
              linkType: 'CooperadoCPESQ',
              functionCode: 'STUDY_COORDINATOR',
              primarySiteId: FIXTURE_IDS.sitePilotSede,
            },
          ],
        },
        networkAccess: { hasAccess: false, roles: [] },
        contexts: [pilotContext()],
        defaultContextHint: FIXTURE_TENANT_CODES.pilot,
        session,
      };
    case 'multi':
      return {
        user: user(
          '01926f3e-0000-7000-8000-0000000000u2',
          'ana@centro.example',
          'Ana Souza',
          'pt-BR',
        ),
        person: {
          id: '01926f3e-0000-7000-8000-0000000000p1',
          activeLinks: [
            {
              id: '01926f3e-0000-7000-8000-0000000000l1',
              legalEntityId: FIXTURE_IDS.legalEntityPilot,
              tenantId: FIXTURE_IDS.tenantPilot,
              linkType: 'CooperadoCPESQ',
              functionCode: 'STUDY_COORDINATOR',
              primarySiteId: FIXTURE_IDS.sitePilotSede,
            },
            {
              id: '01926f3e-0000-7000-8000-0000000000l2',
              legalEntityId: FIXTURE_IDS.legalEntitySecond,
              tenantId: FIXTURE_IDS.tenantSecond,
              linkType: 'PJ',
              functionCode: 'STUDY_COORDINATOR',
              primarySiteId: FIXTURE_IDS.siteSecondUnidade,
            },
          ],
        },
        networkAccess: { hasAccess: false, roles: [] },
        contexts: [pilotContext(), secondContext()],
        defaultContextHint: null,
        session,
      };
    case 'network-only':
      return {
        user: user(
          '01926f3e-0000-7000-8000-0000000000u3',
          'carla@giyus.com.br',
          'Carla Admin',
          'pt-BR',
        ),
        person: {
          id: '01926f3e-0000-7000-8000-0000000000p3',
          activeLinks: [
            {
              id: '01926f3e-0000-7000-8000-0000000000l4',
              legalEntityId: '01926f3e-0000-7000-8000-0000000000e0',
              tenantId: null,
              linkType: 'CLT',
              functionCode: 'HOLDING_ADMIN',
              primarySiteId: null,
            },
          ],
        },
        networkAccess: { hasAccess: true, roles: ['HOLDING_ADMIN'] },
        contexts: [],
        defaultContextHint: null,
        session,
      };
    case 'network-quality':
      return {
        user: user(
          FIXTURE_NETWORK_USERS.quality,
          'quiteria@giyus.com.br',
          'Quitéria Alves',
          'pt-BR',
        ),
        person: {
          id: '01926f3e-0000-7000-8000-0000000000p5',
          activeLinks: [
            {
              id: '01926f3e-0000-7000-8000-0000000000l6',
              legalEntityId: '01926f3e-0000-7000-8000-0000000000e0',
              tenantId: null,
              linkType: 'CLT',
              functionCode: 'QUALITY_LEAD',
              primarySiteId: null,
            },
          ],
        },
        networkAccess: { hasAccess: true, roles: ['QUALITY_LEAD'] },
        contexts: [],
        defaultContextHint: null,
        session,
      };
    case 'network-dpo':
      return {
        user: user(FIXTURE_NETWORK_USERS.dpo, 'dora@giyus.com.br', 'Dora Privacidade', 'pt-BR'),
        person: {
          id: '01926f3e-0000-7000-8000-0000000000p6',
          activeLinks: [
            {
              id: '01926f3e-0000-7000-8000-0000000000l7',
              legalEntityId: '01926f3e-0000-7000-8000-0000000000e0',
              tenantId: null,
              linkType: 'CLT',
              functionCode: 'DPO',
              primarySiteId: null,
            },
          ],
        },
        networkAccess: { hasAccess: true, roles: ['DPO'] },
        contexts: [],
        defaultContextHint: null,
        session,
      };
    case 'tenant-investigator':
      return {
        user: user(
          '01926f3e-0000-7000-8000-0000000000u8',
          'paula@centro.example',
          'Paula Investigadora',
          'pt-BR',
        ),
        person: {
          id: '01926f3e-0000-7000-8000-0000000000p8',
          activeLinks: [
            {
              id: '01926f3e-0000-7000-8000-0000000000l8',
              legalEntityId: FIXTURE_IDS.legalEntityPilot,
              tenantId: FIXTURE_IDS.tenantPilot,
              linkType: 'PJ',
              functionCode: 'PRINCIPAL_INVESTIGATOR',
              primarySiteId: FIXTURE_IDS.sitePilotSede,
            },
          ],
        },
        networkAccess: { hasAccess: false, roles: [] },
        contexts: [
          pilotContext({
            roles: [
              {
                scopeType: 'Study',
                scopeId: FIXTURE_IDS.studyPilot,
                role: 'PRINCIPAL_INVESTIGATOR',
              },
            ],
          }),
        ],
        defaultContextHint: FIXTURE_TENANT_CODES.pilot,
        session,
      };
    case 'tenant-break-glass':
      // O investigador principal com um acesso emergencial em vigor (OPEN-078): só escopo e prazos — nunca a razão declarada.
      return {
        user: user(
          '01926f3e-0000-7000-8000-0000000000u9',
          'paulo@centro.example',
          'Paulo Investigador',
          'pt-BR',
        ),
        person: {
          id: '01926f3e-0000-7000-8000-0000000000p9',
          activeLinks: [
            {
              id: '01926f3e-0000-7000-8000-0000000000l9',
              legalEntityId: FIXTURE_IDS.legalEntityPilot,
              tenantId: FIXTURE_IDS.tenantPilot,
              linkType: 'PJ',
              functionCode: 'PRINCIPAL_INVESTIGATOR',
              primarySiteId: FIXTURE_IDS.sitePilotSede,
            },
          ],
        },
        networkAccess: { hasAccess: false, roles: [] },
        contexts: [
          pilotContext({
            roles: [
              {
                scopeType: 'Study',
                scopeId: FIXTURE_IDS.studyPilot,
                role: 'PRINCIPAL_INVESTIGATOR',
              },
            ],
            breakGlass: [
              {
                scopeType: 'Study',
                scopeId: FIXTURE_IDS.studyEmergency,
                expiresAt: BREAK_GLASS_FIXTURE_EXPIRES_AT,
                reviewDueAt: '2099-09-25T13:02:11Z',
              },
            ],
          }),
        ],
        defaultContextHint: FIXTURE_TENANT_CODES.pilot,
        session,
      };
    case 'tenant-delegate':
      // GIY-075: a enfermeira delegada. O contexto nasce SÓ da delegação ativa no estudo piloto (papel de estudo derivado, sem papel de tenant); há uma
      // segunda delegação, no segundo estudo, que o PI já assinou e ela ainda não aceitou ("Minhas delegações").
      return {
        user: user(
          FIXTURE_DELEGATION.delegateUser,
          'daniela@centro.example',
          'Daniela Enfermeira',
          'pt-BR',
        ),
        person: {
          id: FIXTURE_DELEGATION.delegatePerson,
          activeLinks: [
            {
              id: FIXTURE_DELEGATION.delegateLink,
              legalEntityId: FIXTURE_IDS.legalEntityPilot,
              tenantId: FIXTURE_IDS.tenantPilot,
              linkType: 'CLT',
              functionCode: 'NURSE',
              primarySiteId: FIXTURE_IDS.sitePilotSede,
            },
          ],
        },
        networkAccess: { hasAccess: false, roles: [] },
        contexts: [
          pilotContext({
            roles: [
              { scopeType: 'Study', scopeId: FIXTURE_IDS.studyPilot, role: 'CLINICAL_STAFF' },
            ],
            accessibleStudySiteCount: 1,
            activeLinks: [
              {
                id: FIXTURE_DELEGATION.delegateLink,
                linkType: 'CLT',
                functionCode: 'NURSE',
                primarySiteId: FIXTURE_IDS.sitePilotSede,
              },
            ],
          }),
        ],
        defaultContextHint: FIXTURE_TENANT_CODES.pilot,
        session,
      };
    case 'tenant-lead-coordinator':
      // GIY-075: a coordenadora líder (papel de estudo LEAD_COORDINATOR): prepara e emenda delegações (`delegation.manage`), mas não assina
      // nem revoga (`delegation.sign` é do PI) e, pela matriz A.3, nem lê o log (OPEN-111).
      return {
        user: user(
          FIXTURE_DELEGATION.coordinatorUser,
          'livia@centro.example',
          'Lívia Coordenadora',
          'pt-BR',
        ),
        person: {
          id: FIXTURE_DELEGATION.coordinatorPerson,
          activeLinks: [
            {
              id: FIXTURE_DELEGATION.coordinatorLink,
              legalEntityId: FIXTURE_IDS.legalEntityPilot,
              tenantId: FIXTURE_IDS.tenantPilot,
              linkType: 'CooperadoCPESQ',
              functionCode: 'LEAD_STUDY_COORDINATOR',
              primarySiteId: FIXTURE_IDS.sitePilotSede,
            },
          ],
        },
        networkAccess: { hasAccess: false, roles: [] },
        contexts: [
          pilotContext({
            roles: [
              { scopeType: 'Study', scopeId: FIXTURE_IDS.studyPilot, role: 'LEAD_COORDINATOR' },
            ],
            accessibleStudySiteCount: 1,
            activeLinks: [
              {
                id: FIXTURE_DELEGATION.coordinatorLink,
                linkType: 'CooperadoCPESQ',
                functionCode: 'LEAD_STUDY_COORDINATOR',
                primarySiteId: FIXTURE_IDS.sitePilotSede,
              },
            ],
          }),
        ],
        defaultContextHint: FIXTURE_TENANT_CODES.pilot,
        session,
      };
    case 'network-people':
      return {
        user: user(FIXTURE_PEOPLE.adminUser, 'rita@giyus.com.br', 'Rita Recursos Humanos', 'pt-BR'),
        person: {
          id: FIXTURE_PEOPLE.adminPerson,
          activeLinks: [
            {
              id: FIXTURE_PEOPLE.adminLink,
              legalEntityId: '01926f3e-0000-7000-8000-0000000000e0',
              tenantId: null,
              linkType: 'CLT',
              functionCode: 'HOLDING_STAFF',
              primarySiteId: null,
            },
          ],
        },
        networkAccess: { hasAccess: true, roles: ['PEOPLE_ADMIN'] },
        contexts: [],
        defaultContextHint: null,
        session,
      };
    case 'network-legal':
      return {
        user: user(FIXTURE_PEOPLE.legalUser, 'lucia@giyus.com.br', 'Lúcia Jurídico', 'pt-BR'),
        person: {
          id: FIXTURE_PEOPLE.legalPerson,
          activeLinks: [
            {
              id: FIXTURE_PEOPLE.legalLink,
              legalEntityId: '01926f3e-0000-7000-8000-0000000000e0',
              tenantId: null,
              linkType: 'CLT',
              functionCode: 'HOLDING_STAFF',
              primarySiteId: null,
            },
          ],
        },
        networkAccess: { hasAccess: true, roles: ['LEGAL'] },
        contexts: [],
        defaultContextHint: null,
        session,
      };
    case 'tenant-admin': {
      let email = 'tereza@centro.example';
      let displayName = 'Tereza Administradora';
      let isExternalLoginOverride = false;
      try {
        if (
          typeof window !== 'undefined' &&
          window.sessionStorage.getItem('giyus.auth.login_override_external') === '1'
        ) {
          isExternalLoginOverride = true;
          const customEmail = window.sessionStorage.getItem('giyus.auth.login_email')?.trim();
          const customName = window.sessionStorage.getItem('giyus.auth.login_name')?.trim();
          if (customEmail) {
            email = customEmail;
          }
          if (customName) {
            displayName = customName;
          }
        }
      } catch {
        // ignore storage errors
      }
      return {
        user: user(FIXTURE_PEOPLE.centerAdminUser, email, displayName, 'pt-BR'),
        person: {
          id: FIXTURE_PEOPLE.centerAdminPerson,
          activeLinks: [
            {
              id: FIXTURE_PEOPLE.centerAdminLink,
              legalEntityId: FIXTURE_IDS.legalEntityPilot,
              tenantId: FIXTURE_IDS.tenantPilot,
              linkType: 'CLT',
              functionCode: 'TENANT_ADMIN_BOOTSTRAP',
              primarySiteId: FIXTURE_IDS.sitePilotSede,
            },
          ],
        },
        networkAccess: { hasAccess: false, roles: [] },
        contexts: [
          pilotContext({
            twoSites: true,
            roles: isExternalLoginOverride
              ? [
                  { scopeType: 'Tenant', scopeId: FIXTURE_IDS.tenantPilot, role: 'TENANT_ADMIN' },
                  {
                    scopeType: 'Study',
                    scopeId: FIXTURE_IDS.studyPilot,
                    role: 'PRINCIPAL_INVESTIGATOR',
                  },
                ]
              : [{ scopeType: 'Tenant', scopeId: FIXTURE_IDS.tenantPilot, role: 'TENANT_ADMIN' }],
          }),
        ],
        defaultContextHint: FIXTURE_TENANT_CODES.pilot,
        session,
      };
    }
    case 'network+multi':
      return {
        user: user(
          '01926f3e-0000-7000-8000-0000000000u4',
          'diego@giyus.com.br',
          'Diego Startup',
          'en-US',
        ),
        person: {
          id: '01926f3e-0000-7000-8000-0000000000p4',
          activeLinks: [
            {
              id: '01926f3e-0000-7000-8000-0000000000l5',
              legalEntityId: '01926f3e-0000-7000-8000-0000000000e0',
              tenantId: null,
              linkType: 'CLT',
              functionCode: 'STARTUP_SPECIALIST',
              primarySiteId: null,
            },
          ],
        },
        networkAccess: { hasAccess: true, roles: ['STARTUP_SPECIALIST'] },
        contexts: [
          pilotContext({
            twoSites: true,
            roles: [
              { scopeType: 'Tenant', scopeId: FIXTURE_IDS.tenantPilot, role: 'TENANT_ADMIN' },
              { scopeType: 'Tenant', scopeId: FIXTURE_IDS.tenantPilot, role: 'STARTUP_SPECIALIST' },
            ],
          }),
          secondContext(),
        ],
        defaultContextHint: null,
        session,
      };
    case 'syllas-admin': {
      const email = 'syllas@giyus.com.br';
      const displayName = 'Syllas Souza (Usuário Master / Root)';
      return {
        user: user('01926f3e-0000-7000-8000-000000000099', email, displayName, 'pt-BR'),
        person: {
          id: '01926f3e-0000-7000-8000-000000000099',
          activeLinks: [
            {
              id: '01926f3e-0000-7000-8000-00000000009a',
              legalEntityId: '01926f3e-0000-7000-8000-0000000000e0',
              tenantId: null,
              linkType: 'Socio',
              functionCode: 'HOLDING_STAFF',
              primarySiteId: null,
            },
          ],
        },
        networkAccess: {
          hasAccess: true,
          roles: [
            'SUPER_USER',
            'HOLDING_ADMIN',
            'HOLDING_LEADERSHIP',
            'HOLDING_OPERATIONS_PLANNER',
            'STARTUP_SPECIALIST',
            'QUALITY_LEAD',
            'INFOSEC_LEAD',
            'DPO',
            'PRODUCT_OWNER',
            'PEOPLE_ADMIN',
            'LEGAL',
            'SUPPLIER_MANAGER',
          ],
        },
        contexts: [],
        defaultContextHint: 'NETWORK',
        session,
      };
    }
  }
}
